import VRadioGroupVertical from '@voly/ui-components/src/v2/VRadioGroupVertical';
import VStack from '@voly/ui-components/src/v2/VStack.jsx';
import VText from '@voly/ui-components/src/v2/VText.jsx';
import { ReactElement, ReactNode, useState } from 'react';
import BulkEditDialog from './BulkEditDialog';

export type TBulkOption<TValue extends string> = {
  value: TValue;
  label: ReactNode;
};

type TBulkOptionDialogProps<TValue extends string> = {
  title: string;
  sectionLabel: string;
  options: TBulkOption<TValue>[];
  onClose: () => void;
  onSave: (value: TValue) => Promise<void>;
};

const OPTION_GAP = '3';

const BulkOptionDialog = <TValue extends string>({
  title,
  sectionLabel,
  options,
  onClose,
  onSave,
}: TBulkOptionDialogProps<TValue>): ReactElement => {
  const [selected, setSelected] = useState<TValue | undefined>(undefined);

  const handleValueChange = ({ value }: { value: string }): void => {
    setSelected(options.find((option) => option.value === value)?.value);
  };

  const handleSave = (): Promise<void> => {
    if (!selected) {
      return Promise.resolve();
    }

    return onSave(selected);
  };

  return (
    <BulkEditDialog
      title={title}
      canSave={Boolean(selected)}
      onClose={onClose}
      onSave={handleSave}
      body={
        <VStack gap={3} alignItems="flex-start">
          <VText textStyle="sm" fontWeight="semibold">
            {sectionLabel}
          </VText>
          <VRadioGroupVertical
            gap={OPTION_GAP}
            value={selected ?? null}
            options={options}
            onValueChange={handleValueChange}
          />
        </VStack>
      }
    />
  );
};

export default BulkOptionDialog;
